import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import { useAuth } from '../AuthContext';
import { usePermissions } from '../PermissionsContext';
import BradfordFactorWidget from '../BradfordFactorWidget';
import { getHomeRouteForRole } from '../utils/homeRoute';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  CalendarToday as CalendarIcon,
  EventBusy as AbsenceIcon,
  BeachAccess as HolidayIcon,
  Schedule as ScheduleIcon,
  LocalHospital as SspIcon,
  BarChart as ForecastIcon,
  CheckCircle as ApproveIcon,
  TableChart as TimesheetIcon,
  Home as HomeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const TimeDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isOwner, isManager } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingHolidays: 0,
    pendingTimeOff: 0,
    pendingFlexible: 0,
    todayAbsences: 0,
    bradfordAlerts: 0,
  });

  const isManagerOrOwner = isOwner() || isManager();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Pending holiday requests
        try {
          const holidayRes = await api.get('/holidays/pending-count');
          if (holidayRes.data) {
            setStats(prev => ({
              ...prev,
              pendingHolidays: holidayRes.data.count || 0
            }));
          }
        } catch (e) {
          console.log('Could not fetch holiday count');
        }

        // Pending time-off requests
        try {
          const timeOffRes = await api.get('/time-off/pending-count');
          if (timeOffRes.data) {
            setStats(prev => ({
              ...prev,
              pendingTimeOff: timeOffRes.data.count || 0
            }));
          }
        } catch (e) {
          console.log('Could not fetch time-off count');
        }

        // Today's absences
        try {
          const absenceRes = await api.get('/absences/today-count');
          if (absenceRes.data) {
            setStats(prev => ({
              ...prev,
              todayAbsences: absenceRes.data.count || 0
            }));
          }
        } catch (e) {
          console.log('Could not fetch absence count');
        }

      } catch (error) {
        console.error('Error fetching time stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const totalPending = stats.pendingHolidays + stats.pendingTimeOff + stats.pendingFlexible;

  const quickStats = [
    {
      title: 'Pending Approvals',
      value: totalPending,
      color: totalPending > 0 ? '#FF9800' : '#4CAF50',
      subtitle: 'Requests awaiting action',
      action: () => navigate('/time-off/approval')
    },
    {
      title: 'Absent Today',
      value: stats.todayAbsences,
      color: stats.todayAbsences > 0 ? '#f44336' : '#4CAF50',
      subtitle: 'Staff not in today',
      action: () => navigate('/bradford-factor')
    },
    {
      title: 'Bradford Alerts',
      value: stats.bradfordAlerts,
      color: stats.bradfordAlerts > 0 ? '#f44336' : '#4CAF50',
      subtitle: 'Trigger point reached',
      action: () => navigate('/bradford-factor')
    },
    {
      title: 'Approve Requests',
      value: stats.pendingHolidays,
      color: stats.pendingHolidays > 0 ? '#FF9800' : '#4CAF50',
      subtitle: 'Awaiting approval',
      action: () => navigate('/time-off/approval')
    },
  ];

  const managerActions = [
    {
      id: 'rota',
      title: 'Staff Rota',
      description: 'Build and publish weekly rotas. Assign shifts, use templates, and manage the team schedule.',
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #667eea, #764ba2)',
      path: '/rota',
      status: 'Manage rota',
      quickActions: [
        { label: 'View Current Rota', path: '/rota' },
        { label: 'Build New Week', path: '/rota' },
        { label: 'Shift Templates', path: '/rota' },
      ]
    },
    {
      id: 'approve-requests',
      title: 'Approve Requests',
      description: 'Review and action pending holiday, time-off, and flexible working requests.',
      icon: <ApproveIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #11998e, #38ef7d)',
      path: '/time-off/approval',
      status: totalPending > 0 ? `${totalPending} pending` : 'All clear',
      statusColor: totalPending > 0 ? 'warning' : 'default',
      quickActions: [
        { label: 'All Leave Requests', path: '/time-off/approval' },
        { label: 'Time-Off Requests', path: '/time-off/approval' },
        { label: 'Flexible Working', path: '/flexible-working/approval' },
      ]
    },
    {
      id: 'leave-balances',
      title: 'Leave Balances',
      description: 'View holiday entitlement and usage across all team members.',
      icon: <CalendarIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #10b981, #059669)',
      path: '/leave-balances',
      status: 'View report',
      quickActions: [
        { label: 'View All Balances', path: '/leave-balances' },
        { label: 'Export CSV', path: '/leave-balances' },
        { label: 'Holiday Calendar', path: '/time-off/approval' },
      ]
    },
    {
      id: 'ssp-calculator',
      title: 'SSP Calculator',
      description: 'Calculate Statutory Sick Pay entitlement for sickness absences.',
      icon: <SspIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
      path: '/ssp-calculator',
      status: 'Calculate SSP',
      quickActions: [
        { label: 'Calculate SSP', path: '/ssp-calculator' },
        { label: 'View Absences', path: '/submit_absence' },
        { label: 'SSP Rates Info', path: '/ssp-calculator' },
      ]
    },
    {
      id: 'timesheet',
      title: 'Timesheet',
      description: 'View clock in/out records, manage timesheets, and track actual hours worked.',
      icon: <TimesheetIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      path: '/timesheet',
      status: 'View records',
      quickActions: [
        { label: 'Live Now', path: '/timesheet' },
        { label: 'Weekly Timesheet', path: '/timesheet' },
        { label: 'Add Manual Entry', path: '/timesheet' },
      ]
    },
    {
      id: 'absence-tracking',
      title: 'Absence Tracking',
      description: 'Record and manage sickness absences, return-to-work interviews, and fit notes.',
      icon: <AbsenceIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #ef4444, #dc2626)',
      path: '/submit_absence',
      status: stats.todayAbsences > 0 ? `${stats.todayAbsences} absent today` : 'No absences today',
      statusColor: stats.todayAbsences > 0 ? 'error' : 'default',
      quickActions: [
        { label: 'Record Absence', path: '/submit_absence' },
        { label: 'View Absence History', path: '/submit_absence' },
        { label: 'Bradford Factor', path: '/bradford-factor' },
      ]
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #0891b2, #0e7490)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 2
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate(getHomeRouteForRole(user?.role))}
            sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Back to Hub
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Time & Attendance
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Holiday management, rota scheduling, absence tracking, and timesheets
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg">

        {/* Pending approvals alert banner */}
        {totalPending > 0 && isManagerOrOwner && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 4, cursor: 'pointer', '&:hover': { opacity: 0.9 } }}
            onClick={() => navigate('/time-off/approval')}
          >
            <strong>{totalPending} request{totalPending !== 1 ? 's' : ''} pending your approval</strong>
            {stats.pendingHolidays > 0 && ` · ${stats.pendingHolidays} holiday`}
            {stats.pendingTimeOff > 0 && ` · ${stats.pendingTimeOff} time-off`}
            {stats.pendingFlexible > 0 && ` · ${stats.pendingFlexible} flexible working`}
            {' — Click to review'}
          </Alert>
        )}

        {/* Quick stats */}
        {isManagerOrOwner && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {quickStats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
                  }}
                  onClick={stat.action}
                >
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color, mb: 0.5 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.subtitle}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Bradford Factor — Managers only */}
        {isManagerOrOwner && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
              Absence Monitoring
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <BradfordFactorWidget onClick={() => navigate('/bradford-factor')} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Manager tools */}
        {isManagerOrOwner && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#2c3e50' }}>
              Manager Tools
            </Typography>
            <Grid container spacing={3}>
              {managerActions.map((action) => (
                <Grid item xs={12} md={6} key={action.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid #e9ecef',
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ width: 56, height: 56, background: action.iconBg }}>
                          {action.icon}
                        </Avatar>
                        <Chip
                          label={action.status}
                          size="small"
                          color={action.statusColor || 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {action.description}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                          QUICK ACTIONS:
                        </Typography>
                        {action.quickActions.map((qa, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(qa.path); }}
                            sx={{
                              display: 'block',
                              textAlign: 'left',
                              textTransform: 'none',
                              mb: 0.5,
                              justifyContent: 'flex-start',
                              color: '#667eea',
                              '&:hover': { bgcolor: '#667eea10' }
                            }}
                          >
                            → {qa.label}
                          </Button>
                        ))}
                      </Box>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate(action.path)}
                        sx={{
                          background: action.iconBg,
                          textTransform: 'none',
                          py: 1.5,
                          fontWeight: 600,
                          '&:hover': { opacity: 0.9 }
                        }}
                      >
                        Open {action.title}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

      </Container>
    </Box>
  );
};

export default TimeDashboard;